import React from "react";
import { Link } from "react-router-dom";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/about.scss";
import "../css/delivery.scss";

const deliveryRegions = [
  ["Germany", "2–4 working days", "Standard tracked delivery"],
  ["European Union", "3–7 working days", "Tracked cross-border delivery"],
  ["Selected international", "6–12 working days", "Availability shown at checkout"],
];

function Delivery() {
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const { shipping } = siteConfig;

  return (
    <main className="delivery info-page" id="main-content">
      <div className="info-shell info-shell--narrow">
        <nav className="info-breadcrumb" aria-label="Breadcrumb">
          <Link to="/" onClick={scrollToTop}>Home</Link>
          <span aria-hidden="true">/</span>
          <span aria-current="page">Shipping</span>
        </nav>

        <header className="info-page__header info-page__hero">
          <p className="info-page__eyebrow">Shipping</p>
          <h1>From our hands to your wardrobe.</h1>
          <p className="info-page__lead">
            Orders are prepared with care and normally leave us within {shipping.dispatchWindow}.
            You will receive an update when the parcel begins its journey.
          </p>
        </header>

        <section className="delivery__summary" aria-label="Shipping summary">
          <article>
            <span>Standard delivery</span>
            <strong>{formatPrice(shipping.standardRate)}</strong>
            <p>For orders below the complimentary-delivery threshold.</p>
          </article>
          <article>
            <span>Complimentary delivery</span>
            <strong>From {formatPrice(shipping.freeThreshold)}</strong>
            <p>Automatically applied to eligible standard-delivery orders.</p>
          </article>
          <article>
            <span>Dispatch</span>
            <strong>{shipping.dispatchWindow}</strong>
            <p>Working-day estimate from order confirmation.</p>
          </article>
        </section>

        <section className="delivery__regions" aria-labelledby="delivery-regions-title">
          <div className="delivery__section-heading">
            <p className="info-page__eyebrow">Estimated journey</p>
            <h2 id="delivery-regions-title">Where we currently deliver.</h2>
          </div>
          <div className="delivery__region-list" role="list">
            {deliveryRegions.map(([region, estimate, service]) => (
              <article role="listitem" key={region}>
                <h3>{region}</h3>
                <strong>{estimate}</strong>
                <p>{service}</p>
              </article>
            ))}
          </div>
          <p className="delivery__fine-print">
            Estimates begin after dispatch and exclude weekends, public holidays
            and customs processing. Available destinations and the final price
            are always confirmed at checkout.
          </p>
        </section>

        <section className="delivery__journey" aria-labelledby="delivery-journey-title">
          <div>
            <p className="info-page__eyebrow">Parcel progress</p>
            <h2 id="delivery-journey-title">A clear route to you.</h2>
          </div>
          <ol>
            <li><span>1</span><div><h3>Order confirmed</h3><p>We check availability and prepare the order details.</p></div></li>
            <li><span>2</span><div><h3>Packed thoughtfully</h3><p>Your pieces are checked and protected for the journey.</p></div></li>
            <li><span>3</span><div><h3>Handed to the carrier</h3><p>A dispatch message shares tracking when the service supports it.</p></div></li>
            <li><span>4</span><div><h3>Delivered</h3><p>Follow the carrier instructions for a safe handover.</p></div></li>
          </ol>
        </section>

        <section className="info-faq delivery__faq" aria-labelledby="delivery-faq-title">
          <div>
            <p className="info-page__eyebrow">Delivery questions</p>
            <h2 id="delivery-faq-title">Before your parcel sets off.</h2>
          </div>
          <div className="info-faq__list">
            <details>
              <summary>Can I change my delivery address?</summary>
              <p>Contact us immediately. We can only update an address before the parcel is processed, and changes are not always possible.</p>
            </details>
            <details>
              <summary>Will I pay duties or import taxes?</summary>
              <p>Orders crossing a customs border may incur local charges unless checkout explicitly states otherwise. These are determined by the destination authority.</p>
            </details>
            <details>
              <summary>What happens if tracking stops updating?</summary>
              <p>Carrier scans can pause between hubs. If the estimate has passed, contact us with your order number and we will help investigate.</p>
            </details>
            <details>
              <summary>Can I collect my order?</summary>
              <p>Collection is only available when it appears as an option during checkout. Please wait for a ready-to-collect message before travelling.</p>
            </details>
          </div>
        </section>

        <aside className="info-page__contact-card">
          <p className="info-page__eyebrow">Need an update?</p>
          <h2>We can help trace the details.</h2>
          <p>Contact our team with your order number at <a href={`mailto:${siteConfig.contact.email}`}>{siteConfig.contact.email}</a>.</p>
          <Link className="info-button" to="/contact" onClick={scrollToTop}>Contact customer care</Link>
        </aside>
      </div>
    </main>
  );
}

export default Delivery;
